/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { FixedSizeList } from 'react-window';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { calendarIcons } from '../../../../shared/constants';
import { setActiveSecondLayerModal } from '../../ducks/calendarActions';

const IconContainer = styled('div')`
  height: 25px;
  width: 25px;
  border-radius: 4px;
  color: ${(props) => props.color || props.theme.colors.text};
  background: ${(props) => props.backgroundColor || 'transparent'};
  border: ${(props) => (props.backgroundColor ? `1px solid ${props.backgroundColor}` : 'none')};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  padding: 4px;
`;

// Custom Option Component with icon (left), name (middle), video icon (right)
const CustomOption = (props) => {
  const {
    innerRef,
    innerProps,
    data,
    isFocused,
  } = props;

  const { colors } = useTheme();
  const dispatch = useDispatch();

  const videoUrl = data.media?.[0]?.mediaUrl;

  // react-select (v2) selects an option on its `onClick` handler and closes the
  // menu, which can unmount the option before a nested onClick fires. Acting on
  // `onMouseDown` (and blocking the subsequent click) reliably opens the lightbox
  // without selecting the exercise.
  const openVideoLightbox = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (videoUrl) {
      dispatch(setActiveSecondLayerModal('videoModal', { url: videoUrl }));
    }
  };

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        cursor: 'pointer',
        backgroundColor: isFocused ? colors.surfaceSecondary : colors.surfaceElevated,
        fontFamily: 'Nunito Sans',
        fontSize: '16px',
        color: colors.text,
      }}
    >
      {/* Left: Icon + Name */}
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <IconContainer
          color={data.iconColor}
          backgroundColor={data.iconBackgroundColor}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon={data.icon}
            color={data.iconColor || colors.text}
          />
        </IconContainer>
        <span>{data.name}</span>
      </div>

      {/* Right: Video icon if media exists - opens the video in a second-layer lightbox */}
      {data.media?.length > 0 && (
        <span
          role='button'
          aria-label='Play exercise video'
          tabIndex={0}
          onMouseDown={openVideoLightbox}
          onClick={(e) => {
            // Block react-select's option selection on click.
            e.stopPropagation();
            e.preventDefault();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openVideoLightbox(e);
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 5,
            cursor: 'pointer',
          }}
        >
          <IcomoonReact
            iconSet={iconSet}
            size={16}
            icon='video-camera'
            color={colors.textSecondary}
            style={{ minWidth: 16 }}
          />
        </span>
      )}
    </div>
  );
};

// Virtualized MenuList component using react-window
const MenuList = (props) => {
  const {
    options,
    children,
    maxHeight,
    getValue,
  } = props;
  const [value] = getValue();
  const initialOffset = options.indexOf(value) * 40; // 40px is the item height

  if (!children || children.length === 0) {
    return null;
  }

  const height = Math.min(maxHeight, children.length * 40);

  return (
    <FixedSizeList
      height={height}
      itemCount={children.length}
      itemSize={40}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </FixedSizeList>
  );
};

const SelectDropdownExercisePickerLocal = ({
  listItems,
  defaultValue,
  selectedOption,
  onExerciseChange,
  placeholder,
  typeToFilter,
  isTags,
}) => {
  const { colors } = useTheme();
  // Build all exercise options (accessories, tags, exercises)
  // Filter by type if typeToFilter is provided
  const exerciseOptions = (listItems || [])
    .filter((exercise) => !typeToFilter || exercise.type === typeToFilter)
    .map((exercise) => ({
      ...exercise,
      name: exercise.name,
      exerciseId: exercise.id,
      icon: isTags ? 'tag' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.icon,
      iconColor: isTags ? '#FF4500' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.iconColor,
      iconBackgroundColor: isTags ? 'rgba(255, 69, 0, 0.2)' : Object.values(calendarIcons).find((iconObj) => iconObj.type === exercise.type)?.containerColor,
    }));

  const currentExercise = selectedOption || defaultValue;

  // While the video lightbox (opened from a video icon in this dropdown) is up,
  // a click in the lightbox blurs react-select's input and would close the menu.
  // Control the menu so it stays open until the lightbox is dismissed.
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isVideoLightboxOpen = useSelector(
    (state) => state.calendar.data.activeSecondLayerModal === 'videoModal',
  );

  // When the lightbox closes, the input is no longer focused (focus went into the
  // video), so the still-open menu wouldn't close on the next outside click.
  // Restore focus so the dropdown behaves normally again (closes in one click).
  const selectRef = useRef(null);
  const wasVideoLightboxOpen = useRef(false);
  useEffect(() => {
    if (wasVideoLightboxOpen.current && !isVideoLightboxOpen && selectRef.current) {
      selectRef.current.focus();
    }
    wasVideoLightboxOpen.current = isVideoLightboxOpen;
  }, [isVideoLightboxOpen]);

  /* react-select 2.x opens the menu on Space and the arrow keys, but its keydown switch has
     an explicit no-op for Enter while the menu is closed. Add it here.

     Safe against double-firing: react-select calls this handler before its own and bails when
     defaultPrevented, and even without preventDefault its Enter case reads the menuIsOpen
     prop, which is still false within this same event tick. The !menuIsOpen guard is what
     leaves Enter free to select the focused option once the menu is up. */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !menuIsOpen) {
      e.preventDefault();
      setMenuIsOpen(true);
    }
  };

  return (
    <Select
      ref={selectRef}
      options={exerciseOptions}
      value={currentExercise}
      onChange={onExerciseChange}
      onKeyDown={handleKeyDown}
      menuIsOpen={isVideoLightboxOpen || menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => {
        // Ignore blur-driven closes while the lightbox is open.
        if (!isVideoLightboxOpen) setMenuIsOpen(false);
      }}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
        /* react-select's own defaults are white / near-black; every part that
           paints has to read the theme or it stays light under Build's dark mode. */
        control: (provided, state) => ({
          ...provided,
          backgroundColor: colors.surfaceInput,
          borderColor: state.isFocused ? colors.hover : colors.borderMedium,
          boxShadow: 'none',
          '&:hover': { borderColor: state.isFocused ? colors.hover : colors.text },
        }),
        input: (provided) => ({ ...provided, color: colors.text }),
        singleValue: (provided) => ({ ...provided, color: colors.text }),
        placeholder: (provided) => ({ ...provided, color: colors.textSecondary }),
        menu: (provided) => ({ ...provided, backgroundColor: colors.surfaceElevated }),
        option: (provided, state) => ({
          ...provided,
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: state.isFocused ? colors.surfaceSecondary : colors.surfaceElevated,
          fontFamily: 'Nunito Sans',
          fontSize: '16px',
          color: colors.text,
        }),
      }}
      components={{
        Option: CustomOption,
        MenuList,
      }}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.exerciseId}
      placeholder={placeholder || 'Select exercise...'}
    />
  );
};

SelectDropdownExercisePickerLocal.propTypes = {
  listItems: PropTypes.arrayOf(PropTypes.shape({})),
  defaultValue: PropTypes.shape({}),
  selectedOption: PropTypes.shape({}),
  onExerciseChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  typeToFilter: PropTypes.string,
  isTags: PropTypes.bool.isRequired,
};

SelectDropdownExercisePickerLocal.defaultProps = {
  listItems: [],
  defaultValue: null,
  selectedOption: null,
  placeholder: 'Select exercise...',
  typeToFilter: null,
};

CustomOption.propTypes = {
  innerRef: PropTypes.func,
  innerProps: PropTypes.shape({}),
  data: PropTypes.shape({
    name: PropTypes.string,
    icon: PropTypes.string,
    iconColor: PropTypes.string,
    iconBackgroundColor: PropTypes.string,
    media: PropTypes.arrayOf(PropTypes.shape({
      mediaUrl: PropTypes.string,
    })),
  }),
  isFocused: PropTypes.bool,
};

CustomOption.defaultProps = {
  innerRef: null,
  innerProps: {},
  data: {},
  isFocused: false,
};

MenuList.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})),
  children: PropTypes.node,
  maxHeight: PropTypes.number,
  getValue: PropTypes.func,
};

MenuList.defaultProps = {
  options: [],
  children: null,
  maxHeight: 300,
  getValue: () => [null],
};

export default SelectDropdownExercisePickerLocal;
